<script setup lang="ts">
  import type { RouteLocationRaw } from "vue-router"

  defineOptions({ name: "AppCtaButton" })

  const props = withDefaults(
    defineProps<{
      /** Route for NuxtLink */
      to?: RouteLocationRaw
      /** External link */
      href?: string
      /** Size preset */
      size?: "mini" | "2xs" | "xs" | "sm" | "md" | "lg" | "xl"
      /** Disabled state */
      disabled?: boolean
      /** Button type, when neither to nor href is set */
      type?: "button" | "submit"
    }>(),
    {
      to: undefined,
      href: undefined,
      size: "md",
      disabled: false,
      type: "button",
    },
  )

  const componentType = computed(() => {
    if (props.to) return resolveComponent("NuxtLink")
    if (props.href) return "a"
    return "button"
  })

  const componentAttrs = computed(() => {
    if (props.to) return { to: props.to }
    if (props.href) return { href: props.href }
    return { type: props.type, disabled: props.disabled }
  })
</script>

<template>
  <div class="app-cta-button" :class="{ 'app-cta-button--disabled': disabled }">
    <!-- Spinning gradient border -->
    <div class="app-cta-button__border" aria-hidden="true"></div>
    <!-- Button -->
    <component
      :is="componentType"
      v-bind="componentAttrs"
      :class="['app-cta-button__btn', `app-cta-button__btn--${size}`]"
    >
      <slot />
    </component>
  </div>
</template>

<style scoped>
  .app-cta-button {
    position: relative;
    display: inline-block;
    padding: 1.5px;
    border-radius: 8px;
    overflow: hidden;
    background: #00a040;
  }

  .app-cta-button__border {
    position: absolute;
    inset: 0;
    background: conic-gradient(
      from 0deg,
      #00c853,
      #00e676,
      #1de9b6,
      #00e676,
      #00c853
    );
    border-radius: 8px;
    animation: app-cta-spin 3s linear infinite;
  }

  .app-cta-button__btn {
    position: relative;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #00c853;
    color: #fff;
    border: none;
    border-radius: 6.5px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    box-sizing: border-box;
  }

  /* Size presets */
  .app-cta-button__btn--mini {
    padding: 0.3rem 0.75rem;
    font-size: 0.8rem;
  }
  .app-cta-button__btn--2xs {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
  }
  .app-cta-button__btn--xs {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
  }
  .app-cta-button__btn--sm {
    padding: 0.5rem 0.875rem;
    font-size: 0.875rem;
  }
  .app-cta-button__btn--md {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
  .app-cta-button__btn--lg {
    padding: 0.75rem 1.25rem;
    font-size: 1rem;
  }
  .app-cta-button__btn--xl {
    padding: 1rem 1.5rem;
    font-size: 1.125rem;
  }

  .app-cta-button__btn:hover:not(:disabled) {
    background: #00e676;
  }

  @keyframes app-cta-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .app-cta-button--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .app-cta-button--disabled .app-cta-button__border {
    animation-play-state: paused;
  }
</style>
